<script setup lang="ts">
import { matchesSearchAccentInsensitive } from '@/utils/string/search-normalize'
import router from '@/router'
import { clearAllStores } from '@/services/clear-stores'
import { useCompanyStore } from '@/store/company'
import { BuildingOffice2Icon, HomeIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { computed, ref } from 'vue'
import { useErrorStore } from '@/store/error'
import { useUserStore } from '@/store/user'

const props = withDefaults(
  defineProps<{
    collapsed?: boolean
  }>(),
  {
    collapsed: false,
  },
)

const userStore = useUserStore()
const localOptionsOpen = ref(false)
const searchTerm = ref('')

const errorStore = useErrorStore()

const companyStore = useCompanyStore()

const companies = computed(() => companyStore.getCompanies)
const currentCompany = computed(() => companyStore.currentCompany)
const filteredCompanies = computed(() => {
  const term = searchTerm.value.trim()
  if (!term) return companies.value

  return companies.value.filter((company) => matchesSearchAccentInsensitive(company.name, term))
})

const selectedCompanyId = computed({
  get: () => {
    if (!companies.value?.length) {
      errorStore.setErrors(['Informamos que não há companhias ativas associadas a esta conta, sendo necessário contatar a administração'])
      return 'no-companies'
    }
    return companyStore.currentCompany?.id ?? null
  },
  set: (value) => {
    const company = companies.value.find((c) => c.id === value)
    if (company) {
      companyStore.setCurrentCompany(company.id)
      userStore.changeDefaultCompany({ company_id: company.id })
      clearAllStores()
      router.push({ name: 'dashboard', params: { companyId: company.id } })
    }
  },
})

const selectCompany = (companyId: number) => {
  selectedCompanyId.value = companyId
  localOptionsOpen.value = false
  searchTerm.value = ''
}
</script>

<template>
  <div class="relative px-4 py-4 lg:px-4">
    <button
      class="flex w-full items-center border border-white/20 bg-white/5 px-3 py-2 text-white hover:bg-white/10"
      :class="props.collapsed ? 'justify-center' : 'justify-between gap-2'"
      title="Local"
      @click="
        () => {
          localOptionsOpen = true
          searchTerm = ''
        }
      ">
      <span class="flex items-center gap-2">
        <HomeIcon class="h-5 w-5" />
        <span v-if="!props.collapsed" class="max-w-[170px] truncate text-sm font-medium">
          {{ currentCompany?.name || 'Selecionar local' }}
        </span>
      </span>
      <span v-if="!props.collapsed" class="text-xs text-slate-300">Alterar</span>
    </button>

    <Teleport to="body">
      <div v-show="localOptionsOpen" class="fixed inset-0 z-[12000]">
        <div class="absolute inset-0 bg-black/50" @click="localOptionsOpen = false"></div>

        <div class="absolute left-1/2 top-1/2 w-full max-w-xl -translate-x-1/2 -translate-y-1/2 border border-stroke bg-white shadow-2xl">
          <div class="flex items-center justify-between border-b border-stroke px-6 py-4">
            <h4 class="text-lg font-semibold text-slate-800">Selecionar local</h4>
            <button class="border border-stroke p-2 text-slate-600 hover:bg-slate-100" @click="localOptionsOpen = false">
              <XMarkIcon class="h-5 w-5" />
            </button>
          </div>

          <div class="max-h-[60vh] overflow-y-auto px-6 py-4">
            <input
              v-model="searchTerm"
              type="text"
              placeholder="Buscar local por nome"
              class="mb-4 w-full border border-stroke px-3 py-2 text-sm text-slate-700 outline-none focus:border-blue" />

            <p v-if="!companies?.length" class="text-sm text-slate-500">Nenhuma empresa alocada.</p>
            <p v-else-if="!filteredCompanies.length" class="text-sm text-slate-500">Nenhum local encontrado.</p>
            <ul v-else class="flex flex-col gap-2">
              <li v-for="company in filteredCompanies" :key="company.id">
                <button
                  class="flex w-full items-center justify-between border px-4 py-3 text-left hover:bg-slate-50"
                  :class="currentCompany?.id === company.id ? 'border-blue bg-blue/5' : 'border-stroke'"
                  @click="selectCompany(company.id)">
                  <span class="flex items-center gap-2">
                    <BuildingOffice2Icon class="h-5 w-5 text-slate-600" />
                    <span class="font-medium text-slate-800">{{ company.name }}</span>
                  </span>
                  <span v-if="currentCompany?.id === company.id" class="text-xs font-medium text-blue">Atual</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
